import { useMemo } from 'react';
import useSet from 'react-use/lib/useSet';
import get from 'lodash/get';
import { useEstatePlanningFlowQuery } from '../../api/useEstatePlanningFlowQuery';
import {
  Body,
  Category,
  CategoryData,
  CollapsableHeadersData,
  FilteredCategoryAndHeaders,
  Header,
  EstateKeys,
  ValueLessHeaderId,
} from './FlowTable.meta';
import { getEstate } from './utils/getEstate';

interface Params {
  isSpouse: boolean;
}

export const useFlowTable = ({ isSpouse }: Params) => {
  const queryInfo = useEstatePlanningFlowQuery({ isSpouse });

  const [, { has: hasShowCollapsed, toggle: toggleShowCollapsed }] = useSet<number>();

  const [, { has: hasHeaderSelected, toggle: toggleHeader }] = useSet<number>();

  const data = useMemo(() => {
    if (!queryInfo.data) {
      return null;
    }

    const { table, tableAdditionalDetails } = queryInfo.data;

    const categoriesData = table.reduce((acc, tableData) => {
      const { header, ...rest } = tableData;

      return {
        ...acc,
        [header.headerCategoryText]: {
          id: header.headerCategory,
          name: header.headerCategoryText,
          additionalInfo: header.additionalInfo,
          total: tableData.total,
          showObligationsExceedLiquidAssetsWarning: header.showObligationsExceedLiquidAssetsWarning,

          headers: [
            ...get(acc, [header.headerCategoryText, 'headers'], []),
            {
              id: header.headerType,
              name: header.headerText,
              collapsed: header.collapsed,
              additionalInfo: header.additionalInfo,
              categoryName: header.headerCategoryText,
              categoryId: header.headerCategory,

              data: {
                headerId: header.headerType,
                collapsed: header.collapsed,
                headerName: header.headerText,
                ...rest,
              },
            },
          ],
        },
      };
    }, {} as Record<string, Category>);

    const categories = Object.values(categoriesData) as Category[];

    const baseHeaders = categories.reduce((acc, category) => {
      return [...acc, ...category.headers];
    }, [] as Header[]);

    const collapsableHeaderIdList = table.reduce((acc, { header }) => {
      const collapsableHeaderId = header.collapsed ? header.headerType : null;

      return [
        ...acc,
        ...(collapsableHeaderId !== null && !acc.includes(collapsableHeaderId)
          ? [collapsableHeaderId]
          : []),
      ];
    }, [] as number[]);

    const collapsableHeadersData = collapsableHeaderIdList.reduce(
      (collapsableHeaderAcc, collapsableHeaderId) => {
        const collapsableHeaders = baseHeaders.reduce((acc, header) => {
          if (header.id !== collapsableHeaderId) {
            return acc;
          }

          return {
            ...acc,
            category: {
              ...get(acc, ['category'], {}),
              [header.categoryId]: header.id,
            },
            header: {
              ...get(acc, ['header'], {}),
              [header.id]: {
                ...get(acc, ['header', header.id], {}),
                categoryId: header.categoryId,
                collapsedData: {
                  ...get(acc, ['header', header.id, 'collapsedData'], {}),
                  ...(header.collapsed ? header : {}),
                },
                singleData: [
                  ...get(acc, ['header', header.id, 'singleData'], []),
                  ...(!header.collapsed ? [header] : []),
                ],
              },
            },
          };
        }, collapsableHeaderAcc);

        return { ...collapsableHeaderAcc, ...collapsableHeaders };
      },
      {} as CollapsableHeadersData,
    );

    const dataList = baseHeaders.reduce((bodyAcc, headerData: Header) => {
      return [...bodyAcc, headerData.data];
    }, [] as CategoryData[]);

    const body = dataList.reduce((tableDataAcc, tableData) => {
      return {
        ...tableDataAcc,
        inEstateAssets: getEstate({
          data: tableData,
          accumulator: tableDataAcc,
          estateKey: EstateKeys.InEstateAssets,
        }),

        outEstateAssets: getEstate({
          data: tableData,
          accumulator: tableDataAcc,
          estateKey: EstateKeys.OutOfEstate,
        }),

        totals: [
          ...get(tableDataAcc, 'totals', []),
          ...(tableData.headerId !== ValueLessHeaderId.Summary
            ? [
                {
                  headerId: tableData.headerId,
                  collapsed: tableData.collapsed,
                  value: get(tableData, ['total'], null),
                  headerName: tableData.headerName,
                },
              ]
            : []),
        ],
      };
    }, {} as Body);

    return {
      categories,
      headers: baseHeaders,
      collapsableHeadersData,
      body,
      tableAdditionalDetails,
    };
  }, [queryInfo]);

  const filteredCategoriesAndHeaders = useMemo((): FilteredCategoryAndHeaders[] => {
    if (!data) {
      return [];
    }

    return Object.values(data.categories).map(categoryValue => {
      const hasCollapsableHeader =
        Object.keys(data.collapsableHeadersData).length !== 0 &&
        Object.keys(data.collapsableHeadersData.category).includes(categoryValue.id.toString());

      const headerId = hasCollapsableHeader
        ? data.collapsableHeadersData.category[categoryValue.id]
        : null;

      const filteredHeaders = categoryValue.headers.filter(header => {
        if (hasHeaderSelected(header.id)) {
          return false;
        }

        if (headerId) {
          const collapsableHeaderName =
            data.collapsableHeadersData.header[headerId].collapsedData.name;

          if (hasCollapsableHeader && hasShowCollapsed(header.id) && headerId === header.id) {
            return header.name === collapsableHeaderName;
          }

          return header.name !== collapsableHeaderName;
        }

        return true;
      });

      return {
        ...categoryValue,
        filteredHeaders,
      };
    });
  }, [data, hasHeaderSelected, hasShowCollapsed]);

  const collapsableHeaderIds =
    data &&
    Object.keys(data.collapsableHeadersData).length !== 0 &&
    Object.keys(data.collapsableHeadersData.header)
      ? Object.keys(data.collapsableHeadersData.header)
      : [];

  return {
    data: {
      queryInfo: {
        ...queryInfo,
        data,
      },
      filteredCategoriesAndHeaders,
      collapsableHeaderIds,
    },

    actions: {
      hasHeaderSelected,
      toggleHeader,
      hasShowCollapsed,
      toggleShowCollapsed,
    },
  };
};
